import React, { useState, useEffect, useMemo } from "react";
import api from "../../api/axiosClient";
import { getFullImageUrl } from "../../utils/getFullImageUrl";

const CATEGORIES = [
  "Woodwork",
  "Metalwork",
  "Textile",
  "Pottery",
  "Jewelry",
  "Leatherwork",
  "Basketry",
  "Other",
];

const EMPTY_FORM = {
  name: "",
  description: "",
  price: "",
  category: "Woodwork",
  stock: "1",
};

const PAGE_SIZE_OPTIONS = [12, 24, 48];

const SORT_OPTIONS = [
  { value: "newest", label: "Newest first" },
  { value: "oldest", label: "Oldest first" },
  { value: "price_asc", label: "Price: low to high" },
  { value: "price_desc", label: "Price: high to low" },
  { value: "stock_asc", label: "Stock: low to high" },
  { value: "stock_desc", label: "Stock: high to low" },
  { value: "name_asc", label: "Name: A to Z" },
];

/* ---------------------------------------------------------------------- */
/* Helpers                                                                */
/* ---------------------------------------------------------------------- */

/**
 * Safely converts a backend image value into a browser URL.
 *
 * Supports:
 *   - absolute URLs
 *   - relative media paths
 *   - ProductImage objects
 *   - null/undefined values
 */
function safeImageUrl(value) {
  if (!value) return "";

  if (typeof value === "object") {
    value = value.image || value.url || value.src || "";
  }

  if (!value || typeof value !== "string") return "";

  try {
    return getFullImageUrl(value);
  } catch {
    return value;
  }
}

/**
 * Normalizes all possible backend image formats into:
 *
 * [
 *   {
 *     url: "...",
 *     id: "...",
 *   }
 * ]
 */
function getProductImages(product) {
  if (!product) return [];

  const result = [];

  /*
   * New multiple-image API:
   *
   * images: [
   *   { id: 1, image: "/media/..." },
   *   { id: 2, image: "/media/..." }
   * ]
   */
  if (Array.isArray(product.images)) {
    product.images.forEach((img, index) => {
      const rawUrl =
        typeof img === "string"
          ? img
          : img?.image || img?.url || img?.src || "";

      const url = safeImageUrl(rawUrl);

      if (url) {
        result.push({
          url,
          id: img?.id ?? null,
          index,
        });
      }
    });
  }

  /*
   * Backward-compatible single image field.
   *
   * Only add it if it isn't already represented in images.
   */
  if (product.image) {
    const url = safeImageUrl(product.image);

    if (
      url &&
      !result.some(existing => existing.url === url)
    ) {
      result.unshift({
        url,
        id: product.image_id ?? null,
        index: -1,
      });
    }
  }

  return result;
}

function getImageUrls(product) {
  return getProductImages(product)
    .map(img => img.url)
    .filter(Boolean);
}

let uid = 0;

const nextUid = () => `img_${Date.now()}_${uid++}`;

/* ---------------------------------------------------------------------- */
/* Multi-image uploader                                                   */
/* ---------------------------------------------------------------------- */

function ImageUploader({ images, setImages }) {
  const inputId = useMemo(
    () => `img-input-${nextUid()}`,
    []
  );

  const handleFiles = fileList => {
    const files = Array.from(fileList || []);

    if (files.length === 0) return;

    const newEntries = files.map(file => ({
      key: nextUid(),
      file,
      preview: URL.createObjectURL(file),
      isExisting: false,
      existingId: null,
    }));

    setImages(prev => [...prev, ...newEntries]);
  };

  const removeAt = key => {
    setImages(prev => {
      const removed = prev.find(img => img.key === key);

      if (
        removed &&
        !removed.isExisting &&
        removed.preview?.startsWith("blob:")
      ) {
        URL.revokeObjectURL(removed.preview);
      }

      return prev.filter(img => img.key !== key);
    });
  };

  return (
    <div>
      <label style={labelStyle}>Product Images</label>

      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 10,
        }}
      >
        {images.map((img, idx) => (
          <div
            key={img.key}
            style={{
              position: "relative",
              width: 96,
              height: 96,
              borderRadius: 10,
              overflow: "hidden",
              background: "#000",
              border:
                idx === 0
                  ? "2px solid #111"
                  : "1px solid #e5e7eb",
            }}
          >
            <img
              src={img.preview}
              alt=""
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                display: "block",
              }}
              onError={e => {
                e.currentTarget.style.display = "none";
              }}
            />

            {idx === 0 && (
              <span
                style={{
                  position: "absolute",
                  bottom: 4,
                  left: 4,
                  background: "rgba(17,17,17,0.85)",
                  color: "#fff",
                  fontSize: 9,
                  fontWeight: 700,
                  padding: "2px 6px",
                  borderRadius: 6,
                }}
              >
                MAIN
              </span>
            )}

            <button
              type="button"
              onClick={() => removeAt(img.key)}
              style={{
                position: "absolute",
                top: 4,
                right: 4,
                background: "rgba(0,0,0,0.65)",
                color: "#fff",
                border: "none",
                borderRadius: "50%",
                width: 22,
                height: 22,
                cursor: "pointer",
                fontSize: 12,
                lineHeight: "22px",
                textAlign: "center",
                padding: 0,
              }}
              aria-label="Remove image"
            >
              ✕
            </button>
          </div>
        ))}

        <div
          onClick={() => {
            const input = document.getElementById(inputId);

            if (input) {
              input.click();
            }
          }}
          style={{
            width: 96,
            height: 96,
            borderRadius: 10,
            border: "2px dashed #e5e7eb",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            color: "#9ca3af",
            flexDirection: "column",
            background: "#fafafa",
          }}
        >
          <div style={{ fontSize: 20 }}>＋</div>
          <div style={{ fontSize: 10 }}>Add</div>
        </div>

        <input
          id={inputId}
          type="file"
          accept="image/*"
          multiple
          onChange={e => {
            handleFiles(e.target.files);
            e.target.value = "";
          }}
          style={{ display: "none" }}
        />
      </div>

      <p
        style={{
          fontSize: 12,
          color: "#9ca3af",
          marginTop: 8,
        }}
      >
        First image is the main photo shown in listings. Click ✕
        to remove, add more anytime.
      </p>
    </div>
  );
}

/* ---------------------------------------------------------------------- */
/* Image gallery                                                          */
/* ---------------------------------------------------------------------- */

function ImageGallery({ urls }) {
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    setIdx(0);
  }, [urls.length]);

  if (!urls || urls.length === 0) {
    return (
      <div
        style={{
          width: "100%",
          height: 160,
          background: "#f3f4f6",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#9ca3af",
          fontSize: 13,
        }}
      >
        No image
      </div>
    );
  }

  const currentUrl = urls[idx] || urls[0];

  return (
    <div
      style={{
        position: "relative",
        width: "100%",
        height: 160,
        background: "#f3f4f6",
      }}
    >
      <img
        src={currentUrl}
        alt=""
        style={{
          width: "100%",
          height: "100%",
          objectFit: "cover",
          display: "block",
        }}
        onError={e => {
          e.currentTarget.style.display = "none";
        }}
      />

      {urls.length > 1 && (
        <>
          <button
            type="button"
            onClick={e => {
              e.stopPropagation();
              setIdx(
                i => (i - 1 + urls.length) % urls.length
              );
            }}
            style={navArrowStyle("left")}
            aria-label="Previous image"
          >
            ‹
          </button>

          <button
            type="button"
            onClick={e => {
              e.stopPropagation();
              setIdx(
                i => (i + 1) % urls.length
              );
            }}
            style={navArrowStyle("right")}
            aria-label="Next image"
          >
            ›
          </button>

          <div
            style={{
              position: "absolute",
              bottom: 6,
              left: 0,
              right: 0,
              display: "flex",
              justifyContent: "center",
              gap: 4,
            }}
          >
            {urls.map((_, i) => (
              <span
                key={i}
                style={{
                  width: 5,
                  height: 5,
                  borderRadius: "50%",
                  background:
                    i === idx
                      ? "#fff"
                      : "rgba(255,255,255,0.45)",
                }}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

const navArrowStyle = side => ({
  position: "absolute",
  top: "50%",
  [side]: 6,
  transform: "translateY(-50%)",
  background: "rgba(0,0,0,0.5)",
  color: "#fff",
  border: "none",
  borderRadius: "50%",
  width: 26,
  height: 26,
  cursor: "pointer",
  fontSize: 16,
  lineHeight: "26px",
  textAlign: "center",
  padding: 0,
});

/* ---------------------------------------------------------------------- */
/* Shared Add / Edit form                                                 */
/* ---------------------------------------------------------------------- */

function ProductForm({
  mode,
  initialProduct,
  addToast,
  onSaved,
  onCancel,
}) {
  const isEdit = mode === "edit";

  const [form, setForm] = useState(
    isEdit
      ? {
          name: initialProduct.name || "",
          description: initialProduct.description || "",
          price: initialProduct.price ?? "",
          category:
            initialProduct.category || "Woodwork",
          stock: initialProduct.stock ?? "1",
        }
      : { ...EMPTY_FORM }
  );

  const [images, setImages] = useState(() => {
    if (!isEdit) return [];

    return getProductImages(initialProduct).map(
      img => ({
        key: nextUid(),
        file: null,
        preview: img.url,
        isExisting: true,
        existingId: img.id,
      })
    );
  });

  const [saving, setSaving] = useState(false);

  const buildFormData = () => {
    const formData = new FormData();

    formData.append("name", form.name.trim());
    formData.append(
      "description",
      form.description.trim()
    );
    formData.append("price", form.price);
    formData.append("category", form.category);
    formData.append("stock", form.stock);

    const newFiles = images.filter(
      img => !img.isExisting && img.file
    );

    /*
     * Backward-compatible primary image.
     */
    if (newFiles.length > 0) {
      formData.append("image", newFiles[0].file);
    }

    /*
     * Multiple images.
     */
    newFiles.forEach(img => {
      formData.append("images", img.file);
    });

    /*
     * Existing images that the user kept.
     */
    if (isEdit) {
      const keptExistingIds = images
        .filter(
          img =>
            img.isExisting &&
            img.existingId !== null &&
            img.existingId !== undefined
        )
        .map(img => img.existingId);

      formData.append(
        "kept_image_ids",
        JSON.stringify(keptExistingIds)
      );
    }

    return formData;
  };

  const resetForm = () => {
    setForm({ ...EMPTY_FORM });
    setImages([]);
  };

  const submit = async ({ addAnother = false } = {}) => {
    setSaving(true);

    try {
      const formData = buildFormData();

      if (isEdit) {
        await api.patch(
          `/products/${initialProduct.id}/`,
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          }
        );

        addToast(
          "Product updated.",
          "success",
          "Saved ✅"
        );

        onSaved();
      } else {
        await api.post(
          "/products/",
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          }
        );

        addToast(
          "Product submitted! It will appear in the shop once approved by admin.",
          "success",
          "Product Submitted ✅"
        );

        resetForm();

        if (!addAnother) {
          onSaved();
        }
      }
    } catch (err) {
      console.error("Product save error:", err);

      let msg =
        err.response?.data?.detail ||
        err.response?.data?.message;

      if (!msg && err.response?.data) {
        const data = err.response.data;

        if (typeof data === "string") {
          msg = data;
        } else if (typeof data === "object") {
          const firstError = Object.values(data)[0];

          if (Array.isArray(firstError)) {
            msg = firstError.join(", ");
          } else if (typeof firstError === "string") {
            msg = firstError;
          } else {
            msg = JSON.stringify(firstError);
          }
        }
      }

      if (!msg) {
        msg = "Failed to save product.";
      }

      addToast(
        String(msg),
        "error",
        "Error"
      );
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = e => {
    e.preventDefault();
    submit();
  };

  const handleSaveAndAddAnother = e => {
    e.preventDefault();
    submit({ addAnother: true });
  };

  return (
    <form onSubmit={handleSubmit}>
      <div style={{ marginBottom: 20 }}>
        <ImageUploader
          images={images}
          setImages={setImages}
        />
      </div>

      <div style={{ marginBottom: 14 }}>
        <label style={labelStyle}>
          Product Name *
        </label>

        <input
          type="text"
          placeholder="e.g. Handmade Wooden Bowl"
          value={form.name}
          onChange={e =>
            setForm({
              ...form,
              name: e.target.value,
            })
          }
          required
          style={inputStyle}
        />
      </div>

      <div style={{ marginBottom: 14 }}>
        <label style={labelStyle}>
          Description *
        </label>

        <textarea
          placeholder="Describe your product..."
          rows={3}
          value={form.description}
          onChange={e =>
            setForm({
              ...form,
              description: e.target.value,
            })
          }
          required
          style={{
            ...inputStyle,
            resize: "vertical",
          }}
        />
      </div>

      <div
        style={{
          display: "flex",
          gap: 12,
          marginBottom: 14,
        }}
      >
        <div style={{ flex: 1 }}>
          <label style={labelStyle}>
            Price (KES) *
          </label>

          <input
            type="number"
            placeholder="1500"
            min="0"
            step="0.01"
            value={form.price}
            onChange={e =>
              setForm({
                ...form,
                price: e.target.value,
              })
            }
            required
            style={inputStyle}
          />
        </div>

        <div style={{ flex: 1 }}>
          <label style={labelStyle}>
            Stock *
          </label>

          <input
            type="number"
            placeholder="10"
            min="1"
            value={form.stock}
            onChange={e =>
              setForm({
                ...form,
                stock: e.target.value,
              })
            }
            required
            style={inputStyle}
          />
        </div>
      </div>

      <div style={{ marginBottom: 20 }}>
        <label style={labelStyle}>
          Category *
        </label>

        <select
          value={form.category}
          onChange={e =>
            setForm({
              ...form,
              category: e.target.value,
            })
          }
          style={{
            ...inputStyle,
            background: "#fff",
          }}
        >
          {CATEGORIES.map(category => (
            <option
              key={category}
              value={category}
            >
              {category}
            </option>
          ))}
        </select>
      </div>

      <div
        style={{
          display: "flex",
          gap: 10,
        }}
      >
        {isEdit && (
          <button
            type="button"
            onClick={onCancel}
            disabled={saving}
            style={{
              flex: 1,
              padding: 14,
              background: "#fff",
              color: "#374151",
              border: "1px solid #e5e7eb",
              borderRadius: 10,
              fontSize: 15,
              fontWeight: 700,
              cursor: saving
                ? "not-allowed"
                : "pointer",
            }}
          >
            Cancel
          </button>
        )}

        <button
          type="submit"
          disabled={saving}
          style={{
            flex: 1,
            padding: 14,
            background: saving
              ? "#9ca3af"
              : "#111",
            color: "#fff",
            border: "none",
            borderRadius: 10,
            fontSize: 15,
            fontWeight: 700,
            cursor: saving
              ? "not-allowed"
              : "pointer",
          }}
        >
          {saving
            ? "Saving..."
            : isEdit
            ? "Save Changes"
            : "Submit Product for Approval"}
        </button>

        {!isEdit && (
          <button
            type="button"
            onClick={handleSaveAndAddAnother}
            disabled={saving}
            style={{
              flex: 1,
              padding: 14,
              background: "#fff",
              color: "#111",
              border: "1px solid #111",
              borderRadius: 10,
              fontSize: 15,
              fontWeight: 700,
              cursor: saving
                ? "not-allowed"
                : "pointer",
            }}
          >
            Save & Add Another
          </button>
        )}
      </div>
    </form>
  );
}

/* ---------------------------------------------------------------------- */
/* Delete confirmation                                                     */
/* ---------------------------------------------------------------------- */

function ConfirmModal({
  title,
  body,
  confirmLabel,
  danger,
  onCancel,
  onConfirm,
  busy,
}) {
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
        padding: 16,
      }}
      onClick={onCancel}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: "#fff",
          borderRadius: 16,
          padding: "1.75rem",
          maxWidth: 380,
          width: "100%",
          boxShadow:
            "0 10px 40px rgba(0,0,0,0.2)",
        }}
      >
        <h3
          style={{
            fontSize: 17,
            fontWeight: 800,
            margin: "0 0 8px",
          }}
        >
          {title}
        </h3>

        <p
          style={{
            color: "#6b7280",
            fontSize: 14,
            lineHeight: 1.5,
            marginBottom: 20,
          }}
        >
          {body}
        </p>

        <div
          style={{
            display: "flex",
            gap: 10,
          }}
        >
          <button
            onClick={onCancel}
            disabled={busy}
            style={{
              flex: 1,
              padding: 12,
              background: "#fff",
              color: "#374151",
              border: "1px solid #e5e7eb",
              borderRadius: 10,
              fontSize: 14,
              fontWeight: 700,
              cursor: busy
                ? "not-allowed"
                : "pointer",
            }}
          >
            Cancel
          </button>

          <button
            onClick={onConfirm}
            disabled={busy}
            style={{
              flex: 1,
              padding: 12,
              background: busy
                ? "#f3a8a8"
                : danger
                ? "#dc2626"
                : "#111",
              color: "#fff",
              border: "none",
              borderRadius: 10,
              fontSize: 14,
              fontWeight: 700,
              cursor: busy
                ? "not-allowed"
                : "pointer",
            }}
          >
            {busy
              ? "Working..."
              : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------------- */
/* Bulk edit modal                                                        */
/* ---------------------------------------------------------------------- */

function BulkEditModal({
  count,
  onCancel,
  onApply,
  busy,
}) {
  const [changeCategory, setChangeCategory] =
    useState(false);

  const [category, setCategory] =
    useState(CATEGORIES[0]);

  const [changeStock, setChangeStock] =
    useState(false);

  const [stock, setStock] = useState("");

  const canApply =
    (changeCategory || changeStock) &&
    (!changeStock || stock !== "");

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
        padding: 16,
      }}
      onClick={onCancel}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: "#fff",
          borderRadius: 16,
          padding: "1.75rem",
          maxWidth: 420,
          width: "100%",
          boxShadow:
            "0 10px 40px rgba(0,0,0,0.2)",
        }}
      >
        <h3
          style={{
            fontSize: 17,
            fontWeight: 800,
            margin: "0 0 4px",
          }}
        >
          Edit {count} product
          {count !== 1 ? "s" : ""}
        </h3>

        <p
          style={{
            color: "#6b7280",
            fontSize: 13,
            marginBottom: 18,
          }}
        >
          Only checked fields below will be
          updated. Everything else stays as is.
        </p>

        <label
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            marginBottom: 10,
            cursor: "pointer",
          }}
        >
          <input
            type="checkbox"
            checked={changeCategory}
            onChange={e =>
              setChangeCategory(
                e.target.checked
              )
            }
          />

          <span
            style={{
              fontSize: 14,
              fontWeight: 600,
            }}
          >
            Set category to:
          </span>
        </label>

        <select
          disabled={!changeCategory}
          value={category}
          onChange={e =>
            setCategory(e.target.value)
          }
          style={{
            ...inputStyle,
            marginBottom: 16,
            background: "#fff",
            opacity: changeCategory
              ? 1
              : 0.5,
          }}
        >
          {CATEGORIES.map(c => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>

        <label
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            marginBottom: 10,
            cursor: "pointer",
          }}
        >
          <input
            type="checkbox"
            checked={changeStock}
            onChange={e =>
              setChangeStock(
                e.target.checked
              )
            }
          />

          <span
            style={{
              fontSize: 14,
              fontWeight: 600,
            }}
          >
            Set stock to:
          </span>
        </label>

        <input
          type="number"
          min="0"
          placeholder="e.g. 10"
          disabled={!changeStock}
          value={stock}
          onChange={e =>
            setStock(e.target.value)
          }
          style={{
            ...inputStyle,
            marginBottom: 20,
            opacity: changeStock
              ? 1
              : 0.5,
          }}
        />

        <div
          style={{
            display: "flex",
            gap: 10,
          }}
        >
          <button
            onClick={onCancel}
            disabled={busy}
            style={{
              flex: 1,
              padding: 12,
              background: "#fff",
              color: "#374151",
              border: "1px solid #e5e7eb",
              borderRadius: 10,
              fontSize: 14,
              fontWeight: 700,
              cursor: busy
                ? "not-allowed"
                : "pointer",
            }}
          >
            Cancel
          </button>

          <button
            onClick={() =>
              onApply({
                category: changeCategory
                  ? category
                  : undefined,
                stock: changeStock
                  ? stock
                  : undefined,
              })
            }
            disabled={
              busy || !canApply
            }
            style={{
              flex: 1,
              padding: 12,
              background:
                busy || !canApply
                  ? "#9ca3af"
                  : "#111",
              color: "#fff",
              border: "none",
              borderRadius: 10,
              fontSize: 14,
              fontWeight: 700,
              cursor:
                busy || !canApply
                  ? "not-allowed"
                  : "pointer",
            }}
          >
            {busy
              ? "Applying..."
              : "Apply"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------------- */
/* Product card                                                           */
/* ---------------------------------------------------------------------- */

function ProductCard({
  product,
  onEdit,
  onDeleteRequest,
  selectMode,
  selected,
  onToggleSelect,
}) {
  const statusColor = {
    approved: {
      bg: "#f0fdf4",
      color: "#15803d",
      label: "✓ Approved",
    },
    pending: {
      bg: "#fffbeb",
      color: "#92400e",
      label: "⏳ Pending",
    },
    rejected: {
      bg: "#fef2f2",
      color: "#991b1b",
      label: "✗ Rejected",
    },
  };

  const status =
    product.status ||
    (product.is_approved
      ? "approved"
      : "pending");

  const s =
    statusColor[status] ||
    statusColor.pending;

  const urls = getImageUrls(product);

  return (
    <div
      onClick={() =>
        selectMode &&
        onToggleSelect(product.id)
      }
      style={{
        border: selected
          ? "2px solid #111"
          : "1px solid #e5e7eb",
        borderRadius: 12,
        overflow: "hidden",
        background: "#fff",
        boxShadow:
          "0 2px 8px rgba(0,0,0,0.04)",
        display: "flex",
        flexDirection: "column",
        position: "relative",
        cursor: selectMode
          ? "pointer"
          : "default",
      }}
    >
      {selectMode && (
        <div
          style={{
            position: "absolute",
            top: 10,
            left: 10,
            zIndex: 2,
            width: 22,
            height: 22,
            borderRadius: 6,
            background: selected
              ? "#111"
              : "rgba(255,255,255,0.9)",
            border: selected
              ? "none"
              : "1px solid #d1d5db",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#fff",
            fontSize: 13,
            fontWeight: 700,
          }}
        >
          {selected ? "✓" : ""}
        </div>
      )}

      <ImageGallery urls={urls} />

      <div
        style={{
          padding: "1rem",
          display: "flex",
          flexDirection: "column",
          flex: 1,
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent:
              "space-between",
            alignItems: "flex-start",
            marginBottom: 6,
            gap: 8,
          }}
        >
          <h4
            style={{
              fontSize: 15,
              fontWeight: 700,
              margin: 0,
            }}
          >
            {product.name}
          </h4>

          <span
            style={{
              background: s.bg,
              color: s.color,
              fontSize: 11,
              fontWeight: 700,
              padding: "3px 10px",
              borderRadius: 20,
              whiteSpace: "nowrap",
              marginLeft: 8,
            }}
          >
            {s.label}
          </span>
        </div>

        <p
          style={{
            fontSize: 12,
            color: "#6b7280",
            marginBottom: 8,
            lineHeight: 1.5,
          }}
        >
          {product.description?.slice(
            0,
            80
          )}

          {product.description?.length >
            80 && "..."}
        </p>

        <div
          style={{
            display: "flex",
            justifyContent:
              "space-between",
            alignItems: "center",
          }}
        >
          <span
            style={{
              fontWeight: 700,
              fontSize: 16,
            }}
          >
            KES{" "}
            {parseFloat(
              product.price || 0
            ).toLocaleString()}
          </span>

          <span
            style={{
              fontSize: 12,
              color: "#6b7280",
            }}
          >
            Stock:{" "}
            {product.stock ?? "—"}
          </span>
        </div>

        {product.category && (
          <div style={{ marginTop: 8 }}>
            <span
              style={{
                background: "#f3f4f6",
                color: "#374151",
                fontSize: 11,
                padding: "3px 10px",
                borderRadius: 20,
              }}
            >
              {product.category}
            </span>
          </div>
        )}

        {!selectMode && (
          <div
            style={{
              display: "flex",
              gap: 8,
              marginTop: 14,
            }}
          >
            <button
              onClick={e => {
                e.stopPropagation();
                onEdit(product);
              }}
              style={{
                flex: 1,
                padding: "8px 0",
                background: "#fff",
                color: "#111",
                border:
                  "1px solid #e5e7eb",
                borderRadius: 8,
                fontSize: 13,
                fontWeight: 700,
                cursor: "pointer",
              }}
            >
              ✎ Edit
            </button>

            <button
              onClick={e => {
                e.stopPropagation();
                onDeleteRequest(product);
              }}
              style={{
                flex: 1,
                padding: "8px 0",
                background: "#fff",
                color: "#dc2626",
                border:
                  "1px solid #fecaca",
                borderRadius: 8,
                fontSize: 13,
                fontWeight: 700,
                cursor: "pointer",
              }}
            >
              🗑 Delete
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------------- */
/* Filter / sort bar                                                      */
/* ---------------------------------------------------------------------- */

function FilterBar({
  search,
  setSearch,
  category,
  setCategory,
  status,
  setStatus,
  sortBy,
  setSortBy,
}) {
  return (
    <div
      style={{
        display: "flex",
        flexWrap: "wrap",
        gap: 10,
        marginBottom: 18,
        background: "#f9fafb",
        border: "1px solid #e5e7eb",
        borderRadius: 12,
        padding: 12,
      }}
    >
      <input
        type="text"
        placeholder="Search your products..."
        value={search}
        onChange={e =>
          setSearch(e.target.value)
        }
        style={{
          ...inputStyle,
          flex: "2 1 200px",
          background: "#fff",
        }}
      />

      <select
        value={category}
        onChange={e =>
          setCategory(e.target.value)
        }
        style={{
          ...inputStyle,
          flex: "1 1 140px",
          background: "#fff",
        }}
      >
        <option value="">
          All categories
        </option>

        {CATEGORIES.map(c => (
          <option key={c} value={c}>
            {c}
          </option>
        ))}
      </select>

      <select
        value={status}
        onChange={e =>
          setStatus(e.target.value)
        }
        style={{
          ...inputStyle,
          flex: "1 1 140px",
          background: "#fff",
        }}
      >
        <option value="">
          All statuses
        </option>

        <option value="approved">
          Approved
        </option>

        <option value="pending">
          Pending
        </option>

        <option value="rejected">
          Rejected
        </option>
      </select>

      <select
        value={sortBy}
        onChange={e =>
          setSortBy(e.target.value)
        }
        style={{
          ...inputStyle,
          flex: "1 1 160px",
          background: "#fff",
        }}
      >
        {SORT_OPTIONS.map(o => (
          <option
            key={o.value}
            value={o.value}
          >
            {o.label}
          </option>
        ))}
      </select>
    </div>
  );
}

/* ---------------------------------------------------------------------- */
/* Pagination                                                             */
/* ---------------------------------------------------------------------- */

function Pagination({
  page,
  totalPages,
  setPage,
  pageSize,
  setPageSize,
  totalCount,
}) {
  if (totalCount === 0) return null;

  return (
    <div
      style={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        justifyContent:
          "space-between",
        gap: 12,
        marginTop: 20,
      }}
    >
      <span
        style={{
          fontSize: 13,
          color: "#6b7280",
        }}
      >
        {totalCount} product
        {totalCount !== 1 ? "s" : ""} · page{" "}
        {page} of {totalPages}
      </span>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
        }}
      >
        <select
          value={pageSize}
          onChange={e =>
            setPageSize(
              Number(e.target.value)
            )
          }
          style={{
            ...inputStyle,
            width: "auto",
            padding: "6px 10px",
            fontSize: 13,
          }}
        >
          {PAGE_SIZE_OPTIONS.map(n => (
            <option key={n} value={n}>
              {n} / page
            </option>
          ))}
        </select>

        <button
          onClick={() =>
            setPage(p =>
              Math.max(1, p - 1)
            )
          }
          disabled={page === 1}
          style={pageBtnStyle(
            page === 1
          )}
        >
          ‹ Prev
        </button>

        <button
          onClick={() =>
            setPage(p =>
              Math.min(
                totalPages,
                p + 1
              )
            )
          }
          disabled={
            page === totalPages
          }
          style={pageBtnStyle(
            page === totalPages
          )}
        >
          Next ›
        </button>
      </div>
    </div>
  );
}

const pageBtnStyle = disabled => ({
  padding: "6px 14px",
  borderRadius: 8,
  border: "1px solid #e5e7eb",
  background: "#fff",
  fontSize: 13,
  fontWeight: 700,
  color: disabled
    ? "#d1d5db"
    : "#111",
  cursor: disabled
    ? "not-allowed"
    : "pointer",
});

/* ---------------------------------------------------------------------- */
/* Bulk action bar                                                        */
/* ---------------------------------------------------------------------- */

function BulkActionBar({
  selectedCount,
  allSelected,
  onSelectAll,
  onClear,
  onBulkDelete,
  onBulkEdit,
}) {
  return (
    <div
      style={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        gap: 12,
        background: "#111",
        color: "#fff",
        borderRadius: 12,
        padding: "12px 16px",
        marginBottom: 18,
      }}
    >
      <label
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          fontSize: 13,
          cursor: "pointer",
        }}
      >
        <input
          type="checkbox"
          checked={allSelected}
          onChange={onSelectAll}
        />

        Select all on page
      </label>

      <span
        style={{
          fontSize: 13,
          opacity: 0.8,
        }}
      >
        {selectedCount} selected
      </span>

      <div
        style={{
          marginLeft: "auto",
          display: "flex",
          gap: 8,
        }}
      >
        <button
          onClick={onBulkEdit}
          disabled={selectedCount === 0}
          style={{
            padding: "8px 14px",
            borderRadius: 8,
            border: "1px solid #fff",
            background: "transparent",
            color: "#fff",
            fontSize: 13,
            fontWeight: 700,
            cursor:
              selectedCount === 0
                ? "not-allowed"
                : "pointer",
            opacity:
              selectedCount === 0
                ? 0.5
                : 1,
          }}
        >
          ✎ Bulk edit
        </button>

        <button
          onClick={onBulkDelete}
          disabled={selectedCount === 0}
          style={{
            padding: "8px 14px",
            borderRadius: 8,
            border:
              "1px solid #dc2626",
            background: "#dc2626",
            color: "#fff",
            fontSize: 13,
            fontWeight: 700,
            cursor:
              selectedCount === 0
                ? "not-allowed"
                : "pointer",
            opacity:
              selectedCount === 0
                ? 0.5
                : 1,
          }}
        >
          🗑 Delete selected
        </button>

        <button
          onClick={onClear}
          style={{
            padding: "8px 14px",
            borderRadius: 8,
            border:
              "1px solid #4b5563",
            background: "transparent",
            color: "#d1d5db",
            fontSize: 13,
            fontWeight: 700,
            cursor: "pointer",
          }}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------------- */
/* Main tab                                                               */
/* ---------------------------------------------------------------------- */

export default function ProductsTab({
  addToast,
}) {
  const [products, setProducts] =
    useState([]);

  const [loading, setLoading] =
    useState(true);

  const [view, setView] =
    useState("list");

  const [editingProduct, setEditingProduct] =
    useState(null);

  const [deleteTarget, setDeleteTarget] =
    useState(null);

  const [deleting, setDeleting] =
    useState(false);

  const [search, setSearch] =
    useState("");

  const [categoryFilter, setCategoryFilter] =
    useState("");

  const [statusFilter, setStatusFilter] =
    useState("");

  const [sortBy, setSortBy] =
    useState("newest");

  const [page, setPage] =
    useState(1);

  const [pageSize, setPageSize] =
    useState(PAGE_SIZE_OPTIONS[0]);

  const [selectMode, setSelectMode] =
    useState(false);

  const [selectedIds, setSelectedIds] =
    useState(new Set());

  const [bulkDeleteConfirm, setBulkDeleteConfirm] =
    useState(false);

  const [bulkDeleting, setBulkDeleting] =
    useState(false);

  const [bulkEditOpen, setBulkEditOpen] =
    useState(false);

  const [bulkEditing, setBulkEditing] =
    useState(false);

  const fetchProducts = async () => {
    setLoading(true);

    try {
      const { data } =
        await api.get("/products/");

      const items = Array.isArray(data)
        ? data
        : Array.isArray(data?.results)
        ? data.results
        : [];

      setProducts(items);
    } catch (err) {
      console.error(
        "Failed to load products:",
        err
      );

      addToast(
        "Failed to load products.",
        "error",
        "Error"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  useEffect(() => {
    setPage(1);
  }, [
    search,
    categoryFilter,
    statusFilter,
    sortBy,
    pageSize,
  ]);

  const filteredSorted = useMemo(() => {
    let list = [...products];

    if (search.trim()) {
      const q =
        search.trim().toLowerCase();

      list = list.filter(product => {
        const name =
          product.name?.toLowerCase() ||
          "";

        const description =
          product.description?.toLowerCase() ||
          "";

        return (
          name.includes(q) ||
          description.includes(q)
        );
      });
    }

    if (categoryFilter) {
      list = list.filter(
        product =>
          product.category ===
          categoryFilter
      );
    }

    if (statusFilter) {
      list = list.filter(product => {
        const status =
          product.status ||
          (product.is_approved
            ? "approved"
            : "pending");

        return (
          status === statusFilter
        );
      });
    }

    const byNum =
      (key, dir) =>
      (a, b) =>
        (parseFloat(a[key] || 0) -
          parseFloat(b[key] || 0)) *
        dir;

    switch (sortBy) {
      case "price_asc":
        list.sort(
          byNum("price", 1)
        );
        break;

      case "price_desc":
        list.sort(
          byNum("price", -1)
        );
        break;

      case "stock_asc":
        list.sort(
          byNum("stock", 1)
        );
        break;

      case "stock_desc":
        list.sort(
          byNum("stock", -1)
        );
        break;

      case "name_asc":
        list.sort((a, b) =>
          (a.name || "").localeCompare(
            b.name || ""
          )
        );
        break;

      case "oldest":
        list.sort((a, b) => {
          const aDate = a.created_at
            ? new Date(a.created_at).getTime()
            : 0;

          const bDate = b.created_at
            ? new Date(b.created_at).getTime()
            : 0;

          return (
            aDate - bDate ||
            Number(a.id || 0) -
              Number(b.id || 0)
          );
        });
        break;

      case "newest":
      default:
        list.sort((a, b) => {
          const aDate = a.created_at
            ? new Date(a.created_at).getTime()
            : 0;

          const bDate = b.created_at
            ? new Date(b.created_at).getTime()
            : 0;

          return (
            bDate - aDate ||
            Number(b.id || 0) -
              Number(a.id || 0)
          );
        });
        break;
    }

    return list;
  }, [
    products,
    search,
    categoryFilter,
    statusFilter,
    sortBy,
  ]);

  const totalPages = Math.max(
    1,
    Math.ceil(
      filteredSorted.length /
        pageSize
    )
  );

  const safePage = Math.min(
    page,
    totalPages
  );

  const pageItems =
    filteredSorted.slice(
      (safePage - 1) * pageSize,
      safePage * pageSize
    );

  const startEdit = product => {
    setEditingProduct(product);
    setView("edit");
  };

  const cancelEdit = () => {
    setEditingProduct(null);
    setView("list");
  };

  const handleSaved = () => {
    setEditingProduct(null);
    setView("list");
    fetchProducts();
  };

  const confirmDelete =
    async () => {
      if (!deleteTarget) return;

      setDeleting(true);

      try {
        await api.delete(
          `/products/${deleteTarget.id}/`
        );

        addToast(
          `"${deleteTarget.name}" was deleted.`,
          "success",
          "Deleted"
        );

        setProducts(prev =>
          prev.filter(
            product =>
              product.id !==
              deleteTarget.id
          )
        );

        setDeleteTarget(null);
      } catch (err) {
        console.error(
          "Delete product error:",
          err
        );

        const msg =
          err.response?.data?.detail ||
          "Failed to delete product.";

        addToast(
          typeof msg === "string"
            ? msg
            : JSON.stringify(msg),
          "error",
          "Error"
        );
      } finally {
        setDeleting(false);
      }
    };

  const toggleSelectMode =
    () => {
      setSelectMode(v => !v);
      setSelectedIds(new Set());
    };

  const toggleSelect = id => {
    setSelectedIds(prev => {
      const next = new Set(prev);

      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }

      return next;
    });
  };

  const allOnPageSelected =
    pageItems.length > 0 &&
    pageItems.every(product =>
      selectedIds.has(product.id)
    );

  const toggleSelectAllOnPage =
    () => {
      setSelectedIds(prev => {
        const next = new Set(prev);

        if (allOnPageSelected) {
          pageItems.forEach(product =>
            next.delete(product.id)
          );
        } else {
          pageItems.forEach(product =>
            next.add(product.id)
          );
        }

        return next;
      });
    };

  const runBulkDelete =
    async () => {
      const ids =
        Array.from(selectedIds);

      if (ids.length === 0) {
        return;
      }

      setBulkDeleting(true);

      let failed = 0;

      for (const id of ids) {
        try {
          await api.delete(
            `/products/${id}/`
          );
        } catch {
          failed++;
        }
      }

      if (failed === 0) {
        addToast(
          `${ids.length} product${
            ids.length !== 1
              ? "s"
              : ""
          } deleted.`,
          "success",
          "Deleted"
        );
      } else {
        addToast(
          `${failed} product${
            failed !== 1
              ? "s"
              : ""
          } failed to delete.`,
          "error",
          "Partial failure"
        );
      }

      setBulkDeleting(false);
      setBulkDeleteConfirm(false);
      setSelectMode(false);
      setSelectedIds(new Set());

      fetchProducts();
    };

  const runBulkEdit =
    async ({ category, stock }) => {
      const ids =
        Array.from(selectedIds);

      if (ids.length === 0) {
        return;
      }

      setBulkEditing(true);

      let failed = 0;

      for (const id of ids) {
        try {
          const formData =
            new FormData();

          if (category !== undefined) {
            formData.append(
              "category",
              category
            );
          }

          if (stock !== undefined) {
            formData.append(
              "stock",
              stock
            );
          }

          await api.patch(
            `/products/${id}/`,
            formData,
            {
              headers: {
                "Content-Type":
                  "multipart/form-data",
              },
            }
          );
        } catch {
          failed++;
        }
      }

      if (failed === 0) {
        addToast(
          `${ids.length} product${
            ids.length !== 1
              ? "s"
              : ""
          } updated.`,
          "success",
          "Updated"
        );
      } else {
        addToast(
          `${failed} product${
            failed !== 1
              ? "s"
              : ""
          } failed to update.`,
          "error",
          "Partial failure"
        );
      }

      setBulkEditing(false);
      setBulkEditOpen(false);
      setSelectMode(false);
      setSelectedIds(new Set());

      fetchProducts();
    };

  return (
    <div>
      {/* Header */}
      <div
        style={{
          display: "flex",
          justifyContent:
            "space-between",
          alignItems: "center",
          marginBottom: 20,
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        <div>
          <h2
            style={{
              fontSize: 22,
              fontWeight: 800,
              margin: 0,
            }}
          >
            My Products
          </h2>

          <p
            style={{
              color: "#6b7280",
              fontSize: 13,
              margin: "4px 0 0",
            }}
          >
            Products you submit go live
            after admin approval
          </p>
        </div>

        {view === "list" && (
          <div
            style={{
              display: "flex",
              gap: 8,
            }}
          >
            <button
              onClick={
                toggleSelectMode
              }
              style={{
                background: selectMode
                  ? "#111"
                  : "#fff",
                color: selectMode
                  ? "#fff"
                  : "#111",
                border:
                  "1px solid #111",
                padding:
                  "10px 18px",
                borderRadius: 10,
                fontSize: 14,
                fontWeight: 700,
                cursor: "pointer",
              }}
            >
              {selectMode
                ? "Exit Select"
                : "Select"}
            </button>

            <button
              onClick={() =>
                setView(v =>
                  v === "add"
                    ? "list"
                    : "add"
                )
              }
              style={{
                background: "#111",
                color: "#fff",
                border: "none",
                padding:
                  "10px 20px",
                borderRadius: 10,
                fontSize: 14,
                fontWeight: 700,
                cursor: "pointer",
              }}
            >
              + Add Product
            </button>
          </div>
        )}
      </div>

      {/* Add form */}
      {view === "add" && (
        <div
          style={{
            background: "#fff",
            borderRadius: 16,
            padding: "1.5rem",
            border:
              "1px solid #e5e7eb",
            marginBottom: 24,
            boxShadow:
              "0 2px 12px rgba(0,0,0,0.06)",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent:
                "space-between",
              alignItems: "center",
              marginBottom: 20,
            }}
          >
            <h3
              style={{
                fontSize: 18,
                fontWeight: 700,
                margin: 0,
              }}
            >
              New Product
            </h3>

            <button
              onClick={() =>
                setView("list")
              }
              style={{
                background: "none",
                border: "none",
                color: "#6b7280",
                fontSize: 13,
                fontWeight: 700,
                cursor: "pointer",
              }}
            >
              ← Back to Products
            </button>
          </div>

          <ProductForm
            mode="add"
            addToast={addToast}
            onSaved={() => {
              setView("list");
              fetchProducts();
            }}
          />
        </div>
      )}

      {/* Edit form */}
      {view === "edit" &&
        editingProduct && (
          <div
            style={{
              background: "#fff",
              borderRadius: 16,
              padding: "1.5rem",
              border:
                "1px solid #e5e7eb",
              marginBottom: 24,
              boxShadow:
                "0 2px 12px rgba(0,0,0,0.06)",
            }}
          >
            <h3
              style={{
                fontSize: 18,
                fontWeight: 700,
                marginBottom: 20,
              }}
            >
              Edit "{editingProduct.name}"
            </h3>

            <ProductForm
              mode="edit"
              initialProduct={
                editingProduct
              }
              addToast={addToast}
              onSaved={handleSaved}
              onCancel={cancelEdit}
            />
          </div>
        )}

      {/* Products list */}
      {view === "list" && (
        <>
          {!loading &&
            products.length > 0 && (
              <FilterBar
                search={search}
                setSearch={setSearch}
                category={
                  categoryFilter
                }
                setCategory={
                  setCategoryFilter
                }
                status={statusFilter}
                setStatus={
                  setStatusFilter
                }
                sortBy={sortBy}
                setSortBy={setSortBy}
              />
            )}

          {selectMode && (
            <BulkActionBar
              selectedCount={
                selectedIds.size
              }
              allSelected={
                allOnPageSelected
              }
              onSelectAll={
                toggleSelectAllOnPage
              }
              onClear={
                toggleSelectMode
              }
              onBulkDelete={() =>
                setBulkDeleteConfirm(
                  true
                )
              }
              onBulkEdit={() =>
                setBulkEditOpen(
                  true
                )
              }
            />
          )}

          {loading ? (
            <div
              style={{
                textAlign: "center",
                padding: "3rem",
                color: "#6b7280",
              }}
            >
              Loading your products...
            </div>
          ) : products.length ===
            0 ? (
            <div
              style={{
                textAlign: "center",
                padding: "3rem",
                background: "#f9fafb",
                borderRadius: 16,
                border:
                  "2px dashed #e5e7eb",
              }}
            >
              <div
                style={{
                  fontSize: 40,
                  marginBottom: 12,
                }}
              >
                📦
              </div>

              <h3
                style={{
                  fontWeight: 700,
                  marginBottom: 8,
                }}
              >
                No products yet
              </h3>

              <p
                style={{
                  color: "#6b7280",
                  fontSize: 14,
                  marginBottom: 20,
                }}
              >
                Add your first product
                and it will appear in
                the shop after approval.
              </p>

              <button
                onClick={() =>
                  setView("add")
                }
                style={{
                  background: "#111",
                  color: "#fff",
                  border: "none",
                  padding:
                    "10px 24px",
                  borderRadius: 10,
                  fontSize: 14,
                  fontWeight: 700,
                  cursor: "pointer",
                }}
              >
                + Add Your First Product
              </button>
            </div>
          ) : filteredSorted.length ===
            0 ? (
            <div
              style={{
                textAlign: "center",
                padding: "3rem",
                background: "#f9fafb",
                borderRadius: 16,
                border:
                  "2px dashed #e5e7eb",
              }}
            >
              <div
                style={{
                  fontSize: 32,
                  marginBottom: 10,
                }}
              >
                🔍
              </div>

              <h3
                style={{
                  fontWeight: 700,
                  marginBottom: 6,
                }}
              >
                No matching products
              </h3>

              <p
                style={{
                  color: "#6b7280",
                  fontSize: 14,
                }}
              >
                Try a different search
                term or clear your
                filters.
              </p>
            </div>
          ) : (
            <>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns:
                    "repeat(auto-fill, minmax(240px, 1fr))",
                  gap: 16,
                }}
              >
                {pageItems.map(product => (
                  <ProductCard
                    key={product.id}
                    product={product}
                    onEdit={startEdit}
                    onDeleteRequest={
                      setDeleteTarget
                    }
                    selectMode={
                      selectMode
                    }
                    selected={selectedIds.has(
                      product.id
                    )}
                    onToggleSelect={
                      toggleSelect
                    }
                  />
                ))}
              </div>

              <Pagination
                page={safePage}
                totalPages={totalPages}
                setPage={setPage}
                pageSize={pageSize}
                setPageSize={setPageSize}
                totalCount={
                  filteredSorted.length
                }
              />
            </>
          )}
        </>
      )}

      {/* Single delete confirmation */}
      {deleteTarget && (
        <ConfirmModal
          title="Delete product?"
          body={
            <>
              This will permanently
              remove{" "}
              <strong>
                {deleteTarget.name}
              </strong>{" "}
              from your products.
              This can't be undone.
            </>
          }
          confirmLabel="Delete"
          danger
          busy={deleting}
          onCancel={() =>
            !deleting &&
            setDeleteTarget(null)
          }
          onConfirm={confirmDelete}
        />
      )}

      {/* Bulk delete confirmation */}
      {bulkDeleteConfirm && (
        <ConfirmModal
          title={`Delete ${selectedIds.size} product${
            selectedIds.size !== 1
              ? "s"
              : ""
          }?`}
          body="This will permanently remove all selected products. This can't be undone."
          confirmLabel="Delete Selected"
          danger
          busy={bulkDeleting}
          onCancel={() =>
            !bulkDeleting &&
            setBulkDeleteConfirm(
              false
            )
          }
          onConfirm={runBulkDelete}
        />
      )}

      {/* Bulk edit modal */}
      {bulkEditOpen && (
        <BulkEditModal
          count={selectedIds.size}
          busy={bulkEditing}
          onCancel={() =>
            !bulkEditing &&
            setBulkEditOpen(false)
          }
          onApply={runBulkEdit}
        />
      )}
    </div>
  );
}

/* ---------------------------------------------------------------------- */
/* Styles                                                                 */
/* ---------------------------------------------------------------------- */

const labelStyle = {
  display: "block",
  fontWeight: 600,
  marginBottom: 6,
  fontSize: 14,
  color: "#374151",
};

const inputStyle = {
  width: "100%",
  padding: "10px 14px",
  border: "1px solid #e5e7eb",
  borderRadius: 8,
  fontSize: 14,
  outline: "none",
  boxSizing: "border-box",
};